import type {
  ContactOrderCostBenefitContribution,
  ContactOrderCostBenefitContributionKey,
  ContactOrderPlanningPreview,
} from "../../../../../../api/kehrblatt/contact-orders"
import type { CostBenefitEstimate } from "../../../../../../api/kehrblatt/cost-benefit"

const CREDIT_BASIS_POINTS = 10_000
const EFFORT_HUNDREDTHS_PER_MINUTE = 100
const ITEM_ORDER: ContactOrderCostBenefitContributionKey[] = [
  "EMAIL",
  "MOBILE_MESSAGE",
  "PHONE",
  "CONFIRMATION_EMAIL",
]

type AggregateContactOrderCostBenefitOptions = {
  activeEventIds: string[]
  previewByEventId: Record<string, ContactOrderPlanningPreview>
  errorsByEventId: Record<string, string>
  metadata: CostBenefitEstimate | null
}

const ceilDivision = (value: number, divisor: number) =>
  value > 0 ? Math.ceil(value / divisor) : 0

export const aggregateContactOrderCostBenefit = ({
  activeEventIds,
  previewByEventId,
  errorsByEventId,
  metadata,
}: AggregateContactOrderCostBenefitOptions): CostBenefitEstimate | null => {
  const uniqueActiveEventIds = Array.from(new Set(activeEventIds))

  if (!uniqueActiveEventIds.length) return null

  const contributions: ContactOrderCostBenefitContribution[] = []
  for (const eventId of uniqueActiveEventIds) {
    const preview = previewByEventId[eventId]

    if (!preview) {
      if (errorsByEventId[eventId]) continue

      return null
    }

    if (!preview.cost_benefit_contribution) return null

    contributions.push(preview.cost_benefit_contribution)
  }

  const itemByKey = new Map<
    ContactOrderCostBenefitContributionKey,
    { label: string; count: number; expectedCreditsBasisPoints: number }
  >()
  for (const contribution of contributions) {
    for (const item of contribution.cost_items) {
      const current = itemByKey.get(item.key)
      itemByKey.set(item.key, {
        label: current?.label ?? item.label,
        count: (current?.count ?? 0) + item.count,
        expectedCreditsBasisPoints:
          (current?.expectedCreditsBasisPoints ?? 0) + item.expected_credits_basis_points,
      })
    }
  }

  if (!contributions.length) {
    return {
      time_saved_minutes: 0,
      remaining_effort_minutes: 0,
      total_credits: 0,
      cost_breakdown: metadata?.cost_breakdown ?? [],
      assumptions: metadata?.assumptions ?? [],
      warnings: metadata?.warnings ?? [],
    }
  }

  const costBreakdown = ITEM_ORDER.flatMap((key) => {
    const item = itemByKey.get(key)

    return item
      ? [
          {
            label: item.label,
            count: item.count,
            credits: ceilDivision(item.expectedCreditsBasisPoints, CREDIT_BASIS_POINTS),
          },
        ]
      : []
  })

  return {
    time_saved_minutes: contributions.reduce((total, item) => total + item.time_saved_minutes, 0),
    remaining_effort_minutes: ceilDivision(
      contributions.reduce((total, item) => total + item.remaining_effort_minutes_hundredths, 0),
      EFFORT_HUNDREDTHS_PER_MINUTE,
    ),
    total_credits: costBreakdown.reduce((total, item) => total + item.credits, 0),
    cost_breakdown: costBreakdown,
    assumptions: metadata?.assumptions ?? [],
    warnings: metadata?.warnings ?? [],
  }
}
